import { CommonModule } from "@angular/common";
import { Component, computed, signal } from '@angular/core';

import { DocMeta } from "@template-ui/decorators";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { InputConfig, PanelHeaderMetadataItem, PanelHeaderSectionKey, PanelHeaderSeparatorSetting } from "@template-ui/types";

type SeparatorMode = 'none' | 'legend' | 'filtersLegend' | 'all';

interface SeparatorOption {
  id: SeparatorMode;
  label: string;
  value: PanelHeaderSeparatorSetting | undefined;
  description: string;
}

@DocMeta({
  hide: true,
  showcaseTitle: 'Panel header with optional vertical separators',
  showcaseDesc: `
  Demonstrates the new \`verticalSeparators\` input with inline metadata items:
  - Turn separators completely off
  - Include reusable label/value context next to the title
  - Show them for a few specific sections
  - Enable separators for every rendered section
  `
})
@Component({
    selector: 'ui-panel-header-separators-sample, ez-panel-header-separators-sample',
    imports: [UiLibraryModule, CommonModule],
    templateUrl: './vertical-seperator-panel-header.component.html',
    styleUrl: './vertical-seperator-panel-header.component.scss'
})
export class PanelHeaderSeparatorsSampleComponent {
  /** Title shown in the sample header. */
  readonly panelTitle = 'Investigations – separator demo';

  /** Available separator configurations demonstrated by the sample. */
  readonly separatorOptions: SeparatorOption[] = [
    {
      id: 'none',
      label: 'No separators',
      value: undefined,
      description: 'Default behaviour – only gaps between sections.'
    },
    {
      id: 'legend',
      label: 'Legend only',
      value: ['legend'] as PanelHeaderSectionKey[],
      description: 'Add a divider before the legend.'
    },
    {
      id: 'filtersLegend',
      label: 'Filters + legend',
      value: ['metadata', 'standardFilters', 'additionalActions', 'legend'] as PanelHeaderSectionKey[],
      description: 'Show dividers before metadata and the filter utility sections.'
    },
    {
      id: 'all',
      label: 'All sections',
      value: 'all',
      description: 'Opt-in every section that renders.'
    }
  ];

  private readonly selectedMode = signal<SeparatorMode>('none');

  /** Active separator setting derived from the selected sample mode. */
  readonly separatorSetting = computed<PanelHeaderSeparatorSetting | undefined>(() => {
    return this.separatorOptions.find(opt => opt.id === this.selectedMode())?.value;
  });

  /** Example interactive control to show how metadata coexists with other header sections. */
  readonly leftInputs: InputConfig[] = [
    { type: 'text', name: 'searchTerm', label: 'Search', placeholder: 'Enter text…' }
  ];

  /** Example read-only metadata segments displayed next to the title. */
  readonly metadataItems: PanelHeaderMetadataItem[] = [
    { label: 'Queued', value: '10:32 AM' },
    { label: 'Type', value: 'Email' },
    { label: 'Status', value: 'Delivered' }
  ];

  /** Example action area shown after metadata. */
  readonly additionalActions: InputConfig[] = [
    { type: 'button', name: 'refresh', buttonText: 'Refresh' }
  ];

  /** Example legend items used to demonstrate separators on later sections. */
  readonly legendItems = [
    { className: 'legend-good', label: 'On track' },
    { className: 'legend-risk', label: 'At risk' }
  ];

  /** Switches the sample to a different separator configuration. */
  onModeChange(mode: SeparatorMode): void {
    this.selectedMode.set(mode);
  }

  /** Returns whether the provided sample mode is currently active. */
  isSelected(mode: SeparatorMode): boolean {
    return this.selectedMode() === mode;
  }
}
